// Updated UserTradePerformance.jsx with the same improved layout as TradePerformance

import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  LinearProgress,
  CircularProgress,
  Alert,
  Divider,
  Paper,
  Tooltip,
  Chip
} from '@mui/material';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import DonutLargeIcon from '@mui/icons-material/DonutLarge';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import InfoIcon from '@mui/icons-material/Info';
import tradeService from '../services/tradeService';
import authService from '../services/authService';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, ResponsiveContainer } from 'recharts';

const UserTradePerformance = () => {
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [user, setUser] = useState(null);
  
  useEffect(() => {
    const currentUser = authService.getCurrentUser();
    setUser(currentUser);

    const fetchPerformanceMetrics = async () => {
      try {
        // Use 'user' context to get user-specific metrics
        const response = await tradeService.getPerformanceMetrics('user');
        setMetrics(response.metrics);
        setLoading(false);
      } catch (error) {
        console.error('Failed to load performance metrics', error);
        setError('Failed to load your performance metrics. Please try again later.');
        setLoading(false);
      }
    };
    
    fetchPerformanceMetrics();
  }, []);
  
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }
  
  if (error || !metrics) {
    return (
      <Alert severity="error" sx={{ mb: 3 }}>
        {error || 'No performance metrics available'}
      </Alert>
    );
  }

  // If no trades yet
  if (metrics.totalTrades === 0) {
    return (
      <Alert severity="info" sx={{ mb: 3 }}>
        <Typography variant="body1" gutterBottom>
          You don't have any trade history yet to calculate performance metrics.
        </Typography>
        {user?.subscription_plan === 'free' ? (
          <Typography variant="body2">
            Upgrade to a premium plan to access more trading features and real-time signals!
          </Typography>
        ) : (
          <Typography variant="body2">
            Start trading with our premium signals to build your performance history.
          </Typography>
        )}
      </Alert>
    );
  }
  
  const performanceCards = [
    {
      title: 'Win Rate',
      value: `${metrics.winRate}%`,
      icon: <DonutLargeIcon fontSize="large" sx={{ color: 'info.main' }} />,
      progress: parseFloat(metrics.winRate),
      color: 'info'
    },
    {
      title: 'Total Return', 
      value: parseFloat(metrics.totalProfitPercentage) >= 0 ? 
        `+${metrics.totalProfitPercentage}%` : 
        `${metrics.totalProfitPercentage}%`,
      icon: <ShowChartIcon fontSize="large" sx={{ 
        color: parseFloat(metrics.totalProfitPercentage) >= 0 ? 'success.main' : 'error.main' 
      }} />,
      color: parseFloat(metrics.totalProfitPercentage) >= 0 ? 'success' : 'error',
      hasWarning: true,
      leverageChips: true
    },
    {
      title: 'Total Pips',
      value: parseFloat(metrics.totalPips) >= 0 ? 
        `+${metrics.totalPips}` : 
        `${metrics.totalPips}`,
      icon: <ShowChartIcon fontSize="large" sx={{ 
        color: parseFloat(metrics.totalPips) >= 0 ? 'success.main' : 'error.main' 
      }} />,
      color: parseFloat(metrics.totalPips) >= 0 ? 'success' : 'error'
    },
    {
      title: 'Winning Trades',
      value: metrics.winningTrades,
      icon: <TrendingUpIcon fontSize="large" sx={{ color: 'success.main' }} />,
      progress: (metrics.winningTrades / metrics.totalTrades) * 100,
      color: 'success'
    },
    {
      title: 'Losing Trades',
      value: metrics.losingTrades,
      icon: <TrendingDownIcon fontSize="large" sx={{ color: 'error.main' }} />,
      progress: (metrics.losingTrades / metrics.totalTrades) * 100,
      color: 'error'
    }
  ];
  
  // Prepare data for profit chart
  const profitChartData = metrics.recentPerformance || [];

  return (
    <Box sx={{ mb: 4 }}>
      {/* Leverage Disclaimer */}
      <Alert 
        severity="warning" 
        icon={<InfoIcon />}
        sx={{ mb: 3 }}
      >
        <Typography variant="body2" sx={{ fontWeight: 500 }}>
          ⚠️ <strong>Important:</strong> These percentage returns need to be multiplied by your broker's leverage to get real results.
        </Typography>
      </Alert>

      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center' }}>
        <CalendarTodayIcon sx={{ mr: 1, color: 'text.secondary' }} />
        <Typography variant="body2" color="text.secondary">
          Your performance metrics since {metrics.since ? 
            new Date(metrics.since).toLocaleDateString() : 
            'you started trading'
          }
        </Typography>
      </Box>

      <Grid container spacing={3} sx={{ mb: 4 }}>
        {performanceCards.map((card, index) => (
          <Grid item xs={12} sm={6} md={index < 5 ? 2.4 : 12} key={index}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="subtitle1" color="text.secondary">
                    {card.title}
                  </Typography>
                  {card.icon}
                </Box>
                <Typography variant="h4" component="div" gutterBottom sx={{ 
                  fontWeight: 600,
                  color: card.title === 'Total Return' ? 
                    (parseFloat(metrics.totalProfitPercentage) >= 0 ? 'success.main' : 'error.main') : 
                    card.title === 'Total Pips' ?
                    (parseFloat(metrics.totalPips) >= 0 ? 'success.main' : 'error.main') :
                    'text.primary'
                }}>
                  {card.value}
                  {card.hasWarning && (
                    <Tooltip title="⚠️ WARNING: This percentage must be multiplied by your broker's leverage for real returns.">
                      <InfoIcon sx={{ fontSize: 16, ml: 1, color: 'warning.main', cursor: 'help' }} />
                    </Tooltip>
                  )}
                </Typography>
                
                {/* Leverage calculations for Total Return card */}
                {card.leverageChips && (
                  <Box sx={{ mt: 1 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', display: 'block', mb: 0.5 }}>
                      With leverage:
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                      <Chip 
                        label={`1:20 = ${(parseFloat(metrics.totalProfitPercentage) * 20).toFixed(1)}%`}
                        size="small" 
                        variant="outlined"
                        color={parseFloat(metrics.totalProfitPercentage) >= 0 ? 'success' : 'error'}
                        sx={{ fontSize: '0.65rem', height: '22px' }}
                      />
                      <Chip 
                        label={`1:30 = ${(parseFloat(metrics.totalProfitPercentage) * 30).toFixed(1)}%`}
                        size="small" 
                        variant="outlined"
                        color={parseFloat(metrics.totalProfitPercentage) >= 0 ? 'success' : 'error'}
                        sx={{ fontSize: '0.65rem', height: '22px' }}
                      />
                      <Chip 
                        label={`1:500 = ${(parseFloat(metrics.totalProfitPercentage) * 500).toFixed(1)}%`}
                        size="small" 
                        variant="outlined"
                        color={parseFloat(metrics.totalProfitPercentage) >= 0 ? 'success' : 'error'}
                        sx={{ fontSize: '0.65rem', height: '22px' }}
                      />
                    </Box>
                  </Box>
                )}
                
                {card.progress && (
                  <LinearProgress 
                    variant="determinate" 
                    value={card.progress} 
                    color={card.color} 
                    sx={{ height: 8, borderRadius: 4, mt: 1 }}
                  />
                )}
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
      
      {/* Monthly Performance */}
      {metrics.monthly && (
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12}>
            <Paper sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <CalendarTodayIcon sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="h6">This Month's Performance</Typography>
              </Box>
              <Divider sx={{ mb: 2 }} />
              
              <Grid container spacing={3}>
                <Grid item xs={12} md={4}>
                  <Box sx={{ textAlign: 'center', p: 2 }}>
                    <Typography variant="h6" color="text.secondary" gutterBottom>Total Trades</Typography>
                    <Typography variant="h3" fontWeight="bold">{metrics.monthly.trades}</Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Box sx={{ textAlign: 'center', p: 2 }}>
                    <Typography variant="h6" color="text.secondary" gutterBottom>Win Rate</Typography>
                    <Typography 
                      variant="h3" 
                      fontWeight="bold"
                      color={parseFloat(metrics.monthly.winRate) >= 50 ? 'success.main' : 'error.main'}
                    >
                      {metrics.monthly.winRate}%
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Box sx={{ textAlign: 'center', p: 2 }}>
                    <Typography variant="h6" color="text.secondary" gutterBottom>
                      Return
                      <Tooltip title="⚠️ Multiply by your broker's leverage for real returns">
                        <InfoIcon sx={{ fontSize: 14, ml: 0.5, color: 'warning.main' }} />
                      </Tooltip>
                    </Typography>
                    <Typography 
                      variant="h3" 
                      fontWeight="bold"
                      color={parseFloat(metrics.monthly.profitPercentage) >= 0 ? 'success.main' : 'error.main'}
                    >
                      {parseFloat(metrics.monthly.profitPercentage) >= 0 ? "+" : ""}{metrics.monthly.profitPercentage}%
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </Paper>
          </Grid>
        </Grid>
      )}
      
      {/* Profit Chart */}
      {profitChartData.length > 0 && (
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Paper sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <ShowChartIcon sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="h6">
                  Your Recent Performance 
                  <Tooltip title="Cumulative percentage return over time (multiply by leverage for real returns)">
                    <InfoIcon sx={{ fontSize: 16, ml: 1, color: 'warning.main' }} />
                  </Tooltip>
                </Typography>
              </Box>
              <Divider sx={{ mb: 3 }} />
              
              <Box sx={{ height: 300 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart
                    data={profitChartData}
                    margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis 
                      dataKey="date" 
                      tick={{ fontSize: 12 }}
                      tickFormatter={(value) => {
                        const date = new Date(value);
                        return `${date.getMonth() + 1}/${date.getDate()}`;
                      }}
                    />
                    <YAxis 
                      tickFormatter={(value) => `${value.toFixed(1)}%`}
                    />
                    <RechartsTooltip 
                      formatter={(value, name) => [
                        `${parseFloat(value) >= 0 ? '+' : ''}${parseFloat(value).toFixed(2)}%`,
                        'Cumulative Return'
                      ]}
                      labelFormatter={(label) => {
                        const date = new Date(label);
                        return date.toLocaleDateString();
                      }}
                    />
                    <Line 
                      type="monotone" 
                      dataKey="profit" 
                      stroke="#1976d2"
                      strokeWidth={2}
                      dot={{ fill: '#1976d2', strokeWidth: 2, r: 4 }}
                      activeDot={{ r: 6, stroke: '#1976d2', strokeWidth: 2 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </Box>
              
              {/* Additional note under chart */}
              <Box sx={{ mt: 2, p: 2, backgroundColor: 'rgba(237, 108, 2, 0.04)', borderRadius: 1, borderLeft: '4px solid', borderLeftColor: 'warning.main' }}>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center' }}>
                  <InfoIcon sx={{ fontSize: 14, mr: 1, color: 'warning.main' }} />
                  Chart shows your price movement percentages. Multiply by your broker's leverage for actual margin impact.
                </Typography>
              </Box>
            </Paper>
          </Grid>
        </Grid>
      )}
    </Box>
  );
};

export default UserTradePerformance;